import { useEffect } from "react";
import { useAlert } from "./useAlert";
import { getStoredUser } from "@/lib/api";
import {
  subscribeToIncidents,
  getIncident,
  acknowledgeIncident,
} from "@/services/incidentService";

/**
 * Hook to integrate backend incident alerts into the existing Alert Context
 * Automatically connects to incident WebSocket and feeds data into activeAlert
 *
 * Usage: Call this once in App.js or main layout to enable incident alerts
 * Example: useIncidentAlerts()
 */
export function useIncidentAlerts() {
  const { setActiveAlert, acknowledgeAlert } = useAlert();

  useEffect(() => {
    // Subscribe to incident messages
    const unsubscribe = subscribeToIncidents(async (incidentData) => {
      try {
        // Normalize possible wrapper shapes
        incidentData = incidentData?.incident || incidentData?.payload || incidentData;

        const incidentId =
          incidentData.incident_id
          || incidentData.incidentId
          || incidentData.id
          || null;

        // Helper: turn a location field (string or {lat,lon} object) into a readable string
        const resolveLocation = (loc) => {
          if (!loc) return "Unknown";
          if (typeof loc === "string") return loc.trim() || "Unknown";
          if (typeof loc === "object") {
            const parts = [
              loc.address,
              loc.city,
              loc.district,
              loc.state,
            ].filter(Boolean);
            if (parts.length) return parts.join(", ");
            if (loc.lat !== undefined && loc.lon !== undefined)
              return `${loc.lat}, ${loc.lon}`;
            if (loc.lat !== undefined && loc.lng !== undefined)
              return `${loc.lat}, ${loc.lng}`;
          }
          return "Unknown";
        };

        // Helper: build display-ready details array from an incident object
        const buildDetails = (inc) => {
          const details = [];
          const push = (label, value) => {
            if (value !== null && value !== undefined && String(value).trim() !== "")
              details.push({ label, value: String(value).trim() });
          };
          const primaryVehicle = Array.isArray(inc.vehicles)
            ? inc.vehicles.find((v) => v?.role === "PRIMARY") || inc.vehicles[0]
            : null;
          push("Severity", inc.severity);
          push("Status", inc.status);
          push("Type", inc.incident_type || inc.type);
          push("Driver", inc.driver?.name || inc.driver_name);
          push("Vehicle", primaryVehicle?.registration_number || inc.registration_number || inc.vehicle_number);
          push("Description", inc.description);
          return details;
        };

        let alertData = {
          incidentId,
          type: incidentData.incident_type || incidentData.type || "Incident",
          location: resolveLocation(incidentData.location),
          severity: String(incidentData.severity || "high").toLowerCase(),
          service_statuses: incidentData.service_statuses || {},
          vehicles: incidentData.vehicles,
          vehicle: incidentData.vehicle,
          registration_number: incidentData.registration_number,
          vehicle_number: incidentData.vehicle_number,
          timestamp: incidentData.timestamp || new Date().toISOString(),
          status: incidentData.status || "OPEN",
          description: incidentData.description || "",
          details: buildDetails(incidentData),
        };

        // Fetch full details from API if we have an id and data looks minimal
        if (incidentId) {
          try {
            const fullIncident = await getIncident(incidentId);
            if (fullIncident) {
              alertData = {
                ...alertData,
                location: resolveLocation(fullIncident.location) || alertData.location,
                type: fullIncident.incident_type || fullIncident.type || alertData.type,
                severity: String(fullIncident.severity || alertData.severity).toLowerCase(),
                status: fullIncident.status || alertData.status,
                service_statuses: fullIncident.service_statuses || alertData.service_statuses,
                vehicles: fullIncident.vehicles ?? alertData.vehicles,
                vehicle: fullIncident.vehicle ?? alertData.vehicle,
                registration_number: fullIncident.registration_number ?? alertData.registration_number,
                vehicle_number: fullIncident.vehicle_number ?? alertData.vehicle_number,
                description: fullIncident.description || alertData.description,
                details: buildDetails(fullIncident),
              };
            }
          } catch (err) {
            console.error("[useIncidentAlerts] Failed to fetch full incident details:", err);
            // Continue with partial data already set
          }
        }

        // Shared `status` is not per-org. If our org already responded, don't re-alert.
        const myOrgType = getStoredUser()?.org_type;
        if (myOrgType && alertData.service_statuses?.[myOrgType]) {
          return;
        }

        // Set active alert (displays EmergencyAlertModal)
        setActiveAlert(alertData);
      } catch (err) {
        console.error("[useIncidentAlerts] Error processing incident:", err);
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe?.();
    };
  }, [setActiveAlert, acknowledgeAlert]);
}

/**
 * Hook to acknowledge an incident via API and close the alert modal
 *
 * Usage:
 *   const { acknowledgeIncidentAlert } = useIncidentAcknowledge();
 *   await acknowledgeIncidentAlert(incidentId);
 */
export function useIncidentAcknowledge() {
  const { acknowledgeAlert } = useAlert();

  const acknowledgeIncidentAlert = async (incidentId, description = "") => {
    try {
      await acknowledgeIncident(incidentId, description);
      // Close the modal after successful acknowledgment
      acknowledgeAlert();
      return true;
    } catch (err) {
      console.error("[useIncidentAcknowledge] Failed to acknowledge incident:", err);
      throw err;
    }
  };

  return { acknowledgeIncidentAlert };
}
